import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { APP_CONFIG } from 'src/app.config';

/**
 * Routes Policy Orchestrator requests to the correct endpoint based on whether
 * a `sessionId` is present in the browser URL (the third-party flow is launched
 * with a `?sessionId=...` query parameter).
 *
 *   - `sessionId` in URL     -> `policyOrchestratorThirdPartyUrl`
 *                               (the `policyOrchestrator-temp` endpoint)
 *   - no `sessionId` in URL   -> `policyOrchestratorUrl`
 *                               (the `policy-details` endpoint)
 *
 * When `policyOrchestratorThirdPartyUrl` is not configured for the active
 * environment, every request keeps using `policyOrchestratorUrl` so existing
 * behavior is preserved.
 */
export const policyOrchestratorUrlInterceptor: HttpInterceptorFn = (req, next) => {
    const config = inject(APP_CONFIG);
    const thirdPartyUrl = config.policyOrchestratorThirdPartyUrl;

    // Only act on Policy Orchestrator requests, and only when a dedicated
    // third-party endpoint is configured.
    if (!thirdPartyUrl || req.url !== config.policyOrchestratorUrl) {
        return next(req);
    }

    if (hasSessionIdInUrl()) {
        return next(req.clone({ url: thirdPartyUrl }));
    }

    return next(req);
};

/**
 * Returns true when the current browser URL carries a non-empty `sessionId`
 * query parameter. Guarded for non-browser (SSR) execution where `window` is
 * not available.
 */
function hasSessionIdInUrl(): boolean {
    if (typeof window === 'undefined' || !window.location) {
        return false;
    }

    const sessionId = new URLSearchParams(window.location.search).get('sessionId');
    return !!sessionId;
}
